import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import TrebbleButton from '../common/TrebbleButton';
import TrebbleCard from '../common/TrebbleCard';
import TrebbleInput from '../common/TrebbleInput';
import TrebbleSegmented from '../common/TrebbleSegmented';
import ModalHeader from '../common/ModalHeader';
import SelectableOptionCard from '../settings/SelectableOptionCard';
import StripeConfig from '../../models/helper/StripeConfig';
import WorkspacePricingCatalog from '../../models/helper/WorkspacePricingCatalog';

// Fallback pricing used until StripeConfig.fetchPricingCatalog() resolves or
// when the backend pricing endpoint is unavailable. Authoritative values come
// from Stripe via /api/v1/billing/plans. The monthly/yearly numbers are
// sourced from models/helper/WorkspacePricingCatalog.js so every billing
// surface (Change Plan, Upgrade-to-Team, Hour Packs) stays in sync.
//
// Pricing catalog returns yearly totals; annualMonthlyEquiv is the effective
// per-month cost on a yearly plan (yearly / 12) rounded to cents.
function _annualMonthlyEquivForPlan(planType) {
    const entry = WorkspacePricingCatalog.FALLBACK_PLAN_PRICES[planType];
    if (!entry || !entry.yearly) return 0;
    return Math.round((entry.yearly / 12) * 100) / 100;
}

const _CATALOG_PRICES = WorkspacePricingCatalog.FALLBACK_PLAN_PRICES;
const _CATALOG_FEATURES = WorkspacePricingCatalog.FALLBACK_PLAN_FEATURES;

const FALLBACK_PLAN_CATALOG = {
    starter: {
        key: 'starter',
        monthlyPrice: _CATALOG_PRICES.starter.monthly,
        annualMonthlyEquiv: _annualMonthlyEquivForPlan('starter'),
        annualSavingsPercent: 20,
        currency: _CATALOG_PRICES.starter.currency,
        features: {
            hours: String(_CATALOG_FEATURES.starter.hours),
            storage: _CATALOG_FEATURES.starter.storageGB + ' GB',
            editors: String(_CATALOG_FEATURES.starter.editors),
        },
    },
    pro: {
        key: 'pro',
        monthlyPrice: _CATALOG_PRICES.pro.monthly,
        annualMonthlyEquiv: _annualMonthlyEquivForPlan('pro'),
        annualSavingsPercent: 20,
        currency: _CATALOG_PRICES.pro.currency,
        features: {
            hours: String(_CATALOG_FEATURES.pro.hours),
            storage: _CATALOG_FEATURES.pro.storageGB + ' GB',
            editors: String(_CATALOG_FEATURES.pro.editors),
        },
    },
    team: {
        key: 'team',
        monthlyPrice: _CATALOG_PRICES.team.monthly,
        annualMonthlyEquiv: _annualMonthlyEquivForPlan('team'),
        annualSavingsPercent: 20,
        currency: _CATALOG_PRICES.team.currency,
        // Team seats are dynamic — starting count is 1 (owner + 1), extras
        // purchased per-seat via extra_seat price. See workspaceTeamAddExtraSeat
        // spec: initial maxMemberSeats = 1. The catalog records 5 included
        // seats for billing, but the upgrade flow starts at 1 so the buyer
        // adds exactly what they need.
        features: {
            hours: String(_CATALOG_FEATURES.team.hours),
            storage: _CATALOG_FEATURES.team.storageGB + ' GB',
            editors: '1',
            editorsFlexible: true,
        },
    },
};

function centsToDollars(cents) {
    const n = Number(cents);
    if (!isFinite(n)) return 0;
    return Math.round(n) / 100;
}

function computeSavingsPercent(monthlyCents, yearlyCents) {
    if (!monthlyCents || !yearlyCents) return 0;
    const yearlyIfPaidMonthly = Number(monthlyCents) * 12;
    if (yearlyIfPaidMonthly <= 0) return 0;
    const saved = yearlyIfPaidMonthly - Number(yearlyCents);
    if (saved <= 0) return 0;
    return Math.round((saved / yearlyIfPaidMonthly) * 100);
}

function formatCurrencyPrefix(currency) {
    return (currency || 'usd').toLowerCase() === 'cad' ? 'CA$' : '$';
}

/**
 * UpgradeToTeamContent implements a 3-step workspace upgrade flow:
 *   Step 1: Select plan + billing cycle + workspace name
 *   Step 2: Stripe payment form
 *   Step 3: Success — invite team members via email
 *
 * @param {Object} props
 * @param {function} props.onStartUpgrade - ({ workspaceName, planType, billingCycle }) => Promise<{ clientSecret, workspaceId }>
 * @param {function} props.onMountStripeElement - (container, subscriptionInfo) => stripeElementsInfo (or Promise)
 * @param {function} props.onCompletePayment - (stripeElementsInfo) => Promise<void>
 * @param {function} props.onFinalizeUpgrade - (workspaceId) => Promise<void>
 * @param {function} props.onCancelUpgrade - (workspaceId) => Promise<void>
 * @param {function} [props.onInvite] - (emails, workspaceId) => Promise<void>
 * @param {function} props.onClose - () => void
 * @param {Object} props.i18nModule
 */
const UpgradeToTeamContent = ({
    onStartUpgrade,
    onMountStripeElement,
    onCompletePayment,
    onFinalizeUpgrade,
    onCancelUpgrade,
    onInvite,
    onClose,
    i18nModule,
    useEditorTheme = true,
    defaultName = '',
    // 'create' (default) or 'upgrade_in_place'. Drives step visibility:
    // in-place upgrade skips the workspace-name step because the existing
    // Free workspace already has a name.
    mode = 'create',
}) => {
    const isInPlace = mode === 'upgrade_in_place';
    const [step, setStep] = useState(1);
    const [selectedPlan, setSelectedPlan] = useState('starter');
    const [workspaceName, setWorkspaceName] = useState(defaultName || '');
    const [billingCycle, setBillingCycle] = useState('monthly');
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [pendingWorkspaceId, setPendingWorkspaceId] = useState(null);
    const [pendingClientSecret, setPendingClientSecret] = useState(null);
        const [stripeElementsInfo, setStripeElementsInfo] = useState(null);
        const [inviteEmails, setInviteEmails] = useState(['']);
        const stripeContainerRef = useRef(null);
        const proceedInFlightRef = useRef(false);
        const paymentInFlightRef = useRef(false);

    // Catalog with fetched prices (from Stripe via backend). Falls back to
    // hardcoded marketing values while loading or if the endpoint is down.
    const [planCatalog, setPlanCatalog] = useState(FALLBACK_PLAN_CATALOG);

    useEffect(() => {
        let cancelled = false;
        if (!StripeConfig || typeof StripeConfig.fetchPricingCatalog !== 'function') {
            return () => {};
        }
        StripeConfig.fetchPricingCatalog().then((catalog) => {
            if (cancelled || !catalog || !Array.isArray(catalog.plans)) return;
            const next = { ...FALLBACK_PLAN_CATALOG };
            catalog.plans.forEach((bp) => {
                if (!bp || !bp.planType) return;
                // Only the paid plans are selectable in this 3-step upgrade flow.
                if (bp.planType !== 'starter' && bp.planType !== 'pro' && bp.planType !== 'team') return;
                const monthlyDollars = centsToDollars(bp.amountMonthly);
                const yearlyDollars = centsToDollars(bp.amountYearly);
                const annualMonthlyEquiv = yearlyDollars > 0 ? Math.round((yearlyDollars / 12) * 100) / 100 : monthlyDollars;
                const annualSavingsPercent = computeSavingsPercent(bp.amountMonthly, bp.amountYearly)
                    || (FALLBACK_PLAN_CATALOG[bp.planType] && FALLBACK_PLAN_CATALOG[bp.planType].annualSavingsPercent)
                    || 0;
                const includedHours = bp.includedHours == null ? null : String(bp.includedHours);
                const includedStorage = bp.includedStorageGB == null ? null : String(bp.includedStorageGB) + ' GB';
                // includedEditors === null means the plan uses dynamic seat
                // allocation (Team). Display the initial starting count (1) and
                // mark editorsFlexible so the UI can indicate extras are added
                // per-seat — do NOT fall back to a possibly-stale fixed count.
                const editorsIsFlexible = bp.includedEditors == null;
                const includedEditors = editorsIsFlexible ? '1' : String(bp.includedEditors);
                next[bp.planType] = {
                    key: bp.planType,
                    monthlyPrice: monthlyDollars,
                    annualMonthlyEquiv: annualMonthlyEquiv,
                    annualSavingsPercent: annualSavingsPercent,
                    currency: (bp.currency || 'usd').toLowerCase(),
                    features: {
                        hours: includedHours != null ? includedHours : (FALLBACK_PLAN_CATALOG[bp.planType] ? FALLBACK_PLAN_CATALOG[bp.planType].features.hours : ''),
                        storage: includedStorage != null ? includedStorage : (FALLBACK_PLAN_CATALOG[bp.planType] ? FALLBACK_PLAN_CATALOG[bp.planType].features.storage : ''),
                        editors: includedEditors,
                        editorsFlexible: editorsIsFlexible,
                    },
                };
            });
            setPlanCatalog(next);
        }).catch((err) => {
            console.warn('UpgradeToTeamContent: pricing catalog fetch failed, using fallback', err);
        });
        return () => { cancelled = true; };
    }, []);

    const t = (key) => window.getI18n(i18nModule, key);

    const formatTemplate = (template, vars) => {
        if (!template) return '';
        let result = template;
        Object.keys(vars).forEach((key) => {
            result = result.replace('${' + key + '}', String(vars[key]));
        });
        return result;
    };

    const plan = planCatalog[selectedPlan] || FALLBACK_PLAN_CATALOG[selectedPlan];
    const currencyPrefix = formatCurrencyPrefix(plan.currency);
    const displayPrice = billingCycle === 'monthly'
        ? currencyPrefix + plan.monthlyPrice.toFixed(2) + '/mo'
        : currencyPrefix + plan.annualMonthlyEquiv.toFixed(2) + '/mo (billed annually)';

    // Mount Stripe Elements when step 2 becomes active and we have a clientSecret
    useEffect(() => {
        if (step === 2 && stripeContainerRef.current && pendingClientSecret && onMountStripeElement) {
            const subscriptionInfo = { clientSecret: pendingClientSecret };
            const result = onMountStripeElement(stripeContainerRef.current, subscriptionInfo);
            if (result && typeof result.then === 'function') {
                result.then((info) => setStripeElementsInfo(info)).catch((err) => {
                    console.error('Failed to mount Stripe Elements:', err);
                    setErrorMessage(t('PAYMENT_FORM_ERROR') || 'Failed to load payment form. Please try again.');
                });
            } else if (result) {
                setStripeElementsInfo(result);
            }
        }
    }, [step, pendingClientSecret]);

        const handleProceedToPayment = async () => {
            // In-place upgrade reuses the existing workspace name (backend ignores
            // the payload `name` in that mode); for 'create' mode the user must
            // still provide a name.
            if (!isInPlace && !workspaceName.trim()) return;
            if (proceedInFlightRef.current) return;
            proceedInFlightRef.current = true;
            setIsLoading(true);
            setErrorMessage(null);
            try {
            const result = await onStartUpgrade({
                workspaceName: isInPlace ? undefined : workspaceName.trim(),
                planType: selectedPlan,
                billingCycle: billingCycle,
            });
            setPendingWorkspaceId(result.workspaceId);
            setPendingClientSecret(result.clientSecret);
            setStep(2);
        } catch (error) {
            const msg = (error && error.message) ? error.message : (t('WORKSPACE_CREATE_ERROR') || 'Could not start upgrade');
            setErrorMessage(msg);
            } finally {
                proceedInFlightRef.current = false;
                setIsLoading(false);
            }
        };

        const handleCompletePayment = async () => {
            if (!stripeElementsInfo) {
                setErrorMessage(t('PAYMENT_FORM_ERROR') || 'Payment form not ready');
                return;
            }
            if (paymentInFlightRef.current) return;
            paymentInFlightRef.current = true;
            setIsLoading(true);
            setErrorMessage(null);
            try {
            await onCompletePayment(stripeElementsInfo);
            await onFinalizeUpgrade(pendingWorkspaceId);
            setStep(3);
            } catch (error) {
                const msg = (error && error.message) ? error.message : (t('PAYMENT_ERROR_TRY_AGAIN') || 'Payment failed. Please try again.');
                setErrorMessage(msg);
            } finally {
                paymentInFlightRef.current = false;
                setIsLoading(false);
            }
        };

    const handleBackToStep1 = async () => {
        if (pendingWorkspaceId && onCancelUpgrade) {
            try {
                await onCancelUpgrade(pendingWorkspaceId);
            } catch (e) {
                console.warn('Failed to cancel workspace upgrade:', e);
            }
        }
        setPendingWorkspaceId(null);
        setPendingClientSecret(null);
        setStripeElementsInfo(null);
        setErrorMessage(null);
        setStep(1);
    };

    const handleAddEmailField = () => setInviteEmails([...inviteEmails, '']);
    const handleEmailChange = (index, value) => {
        const updated = [...inviteEmails];
        updated[index] = value;
        setInviteEmails(updated);
    };
    const handleRemoveEmail = (index) => {
        if (inviteEmails.length <= 1) return;
        setInviteEmails(inviteEmails.filter((_, i) => i !== index));
    };

    const submitInvites = async () => {
        // Filter to non-empty, syntactically valid emails — skip blanks
        // silently so users can submit even if they added extra rows.
        const emailsToSend = inviteEmails
            .map((email) => (email || '').trim())
            .filter((email) => email.length > 0);
        if (emailsToSend.length === 0 || typeof onInvite !== 'function') {
            // Nothing valid to send, or no handler wired — close instead of
            // silently discarding the user's typed list.
            onClose && onClose();
            return;
        }
        setIsLoading(true);
        setErrorMessage(null);
        try {
            await onInvite(emailsToSend, pendingWorkspaceId);
            onClose && onClose();
        } catch (err) {
            console.warn('Failed to send workspace invites:', err);
            const fallback = t('INVITE_FAILED') || 'Failed to send invites. Please try again.';
            setErrorMessage((err && err.message) || fallback);
        } finally {
            setIsLoading(false);
        }
    };

    // Total-step count depends on mode — in-place skips the name step so
    // the user only sees (plan → payment → success) = 3 steps still, since
    // renderStep1 covers "plan + cycle + [name]" as one step. Keep the
    // label total at 3 for both modes; if we add the extra-editors step
    // later (Team-only) we will bump this.
    const totalSteps = 3;
    const stepLabelText = formatTemplate(t('STEP_LABEL') || 'Step ${current} of ${total}', { current: step, total: totalSteps });

    const renderPlanCard = (planKey) => {
        const p = planCatalog[planKey] || FALLBACK_PLAN_CATALOG[planKey];
        const isSelected = selectedPlan === planKey;
        const cardCurrencyPrefix = formatCurrencyPrefix(p.currency);
        const priceDisplay = billingCycle === 'monthly'
            ? cardCurrencyPrefix + p.monthlyPrice.toFixed(2)
            : cardCurrencyPrefix + p.annualMonthlyEquiv.toFixed(2);
        const planNameKey = 'PLAN_' + planKey.toUpperCase();
        const planName = t(planNameKey) || (planKey.charAt(0).toUpperCase() + planKey.slice(1));
        const editorsCount = parseInt(p.features.editors);
        // SelectableOptionCard (shared with Change Plan + Hour Packs) handles
        // light/dark theming via semi-transparent borders and the Trebble
        // accent for the selected state, so the card stays readable on both
        // the default (light) surface and the editor (dark) surface.
        return (
            <SelectableOptionCard
                key={planKey}
                selected={isSelected}
                onSelect={() => setSelectedPlan(planKey)}
                style={{ flex: 1, padding: '12px' }}
            >
                <div style={{ fontWeight: 600, fontSize: '13px', marginBottom: '6px' }}>{planName}</div>
                <div style={{ fontSize: '18px', fontWeight: 700, marginBottom: '6px' }}>
                    {priceDisplay}
                    <span style={{ fontSize: '11px', fontWeight: 400, marginLeft: '2px' }}>/mo</span>
                </div>
                <div style={{ fontSize: '11px', opacity: 0.75, lineHeight: '1.6' }}>
                    <div>{p.features.hours} hrs</div>
                    <div>{p.features.storage}</div>
                    <div>
                        {editorsCount} editor{editorsCount !== 1 ? 's' : ''}
                        {p.features.editorsFlexible ? ' + extras' : ''}
                    </div>
                </div>
                {billingCycle === 'annual' && (
                    <div style={{ fontSize: '10px', color: 'var(--trebble-primary)', marginTop: '4px', fontWeight: 600 }}>
                        {formatTemplate(t('ANNUAL_SAVINGS') || 'Save ${percent}%', { percent: p.annualSavingsPercent })}
                    </div>
                )}
            </SelectableOptionCard>
        );
    };

    const renderStep1 = () => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div>
                <div style={{ fontSize: '13px', fontWeight: 500, marginBottom: '8px' }}>
                    {t('BILLING_CYCLE') || 'Billing cycle'}
                </div>
                <TrebbleSegmented
                    value={billingCycle}
                    onChange={(value) => setBillingCycle(value)}
                    options={[
                        { label: t('MONTHLY') || 'Monthly', value: 'monthly' },
                        {
                            label: (
                                <span style={{ whiteSpace: 'nowrap' }}>
                                    {t('ANNUAL') || 'Annual'}
                                    <span style={{ fontSize: '11px', marginLeft: '6px', opacity: 0.7 }}>
                                        {formatTemplate(t('ANNUAL_SAVINGS') || 'Save ${percent}%', { percent: 20 })}
                                    </span>
                                </span>
                            ),
                            value: 'annual',
                        },
                    ]}
                    block
                    useEditorTheme={useEditorTheme}
                />
            </div>

            <div>
                <div style={{ fontSize: '13px', fontWeight: 500, marginBottom: '8px' }}>
                    {t('SELECT_PLAN') || 'Select plan'}
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                    {renderPlanCard('starter')}
                    {renderPlanCard('pro')}
                    {renderPlanCard('team')}
                </div>
            </div>

            {/* Workspace name input — only shown for Create mode. In-place
                upgrade reuses the existing Free workspace's name so the step
                is skipped to keep the flow short. */}
            {!isInPlace && (
                <div>
                    <div style={{ fontSize: '13px', fontWeight: 500, marginBottom: '8px' }}>
                        {t('WORKSPACE_NAME') || 'Workspace name'}
                    </div>
                    <TrebbleInput
                        value={workspaceName}
                        onChange={(e) => setWorkspaceName(e.target.value)}
                        placeholder={t('WORKSPACE_NAME_PLACEHOLDER') || 'e.g. My Team'}
                        useEditorTheme={useEditorTheme}
                    />
                </div>
            )}

            {errorMessage && (
                <div style={{ color: 'var(--color-error-bright)', fontSize: '13px' }}>{errorMessage}</div>
            )}

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <TrebbleButton onClick={onClose} useEditorTheme={useEditorTheme}>
                    {t('CLOSE') || 'Close'}
                </TrebbleButton>
                <TrebbleButton
                    type="primary"
                    onClick={handleProceedToPayment}
                    disabled={(!isInPlace && !workspaceName.trim()) || isLoading}
                    loading={isLoading}
                    useEditorTheme={useEditorTheme}
                >
                    {t('NEXT') || 'Next'}
                </TrebbleButton>
            </div>
        </div>
    );

    const renderStep2 = () => {
        const planNameKey = 'PLAN_' + selectedPlan.toUpperCase();
        const planName = t(planNameKey) || (selectedPlan.charAt(0).toUpperCase() + selectedPlan.slice(1));
        return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', alignItems: 'flex-start' }}>
                        <div style={{ flex: '1 1 220px', padding: '12px', backgroundColor: 'rgba(128,128,128,0.12)', fontSize: '13px' }}>
                            <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '12px' }}>
                                {t('INVOICE') || 'Invoice'}
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '8px' }}>
                                <span style={{ opacity: 0.7 }}>{t('WORKSPACE_NAME') || 'Workspace'}</span>
                                <span style={{ fontWeight: 500, textAlign: 'right' }}>{workspaceName}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '8px' }}>
                                <span style={{ opacity: 0.7 }}>{t('CURRENT_PLAN') || 'Plan'}</span>
                                <span style={{ fontWeight: 500, textAlign: 'right' }}>
                                    {planName} — {billingCycle === 'monthly' ? (t('MONTHLY') || 'Monthly') : (t('ANNUAL') || 'Annual')}
                                </span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', borderTop: '1px solid rgba(128,128,128,0.35)', paddingTop: '8px' }}>
                                <span style={{ fontWeight: 600 }}>{t('PRICING') || 'Total'}</span>
                                <span style={{ fontWeight: 600, textAlign: 'right' }}>{displayPrice}</span>
                            </div>
                        </div>

                        <div style={{ flex: '1.45 1 340px' }}>
                            <div style={{ fontSize: '13px', fontWeight: 500, marginBottom: '8px' }}>
                                {t('ENTER_PAYMENT') || 'Payment details'}
                            </div>
                            <div
                                ref={stripeContainerRef}
                                style={{ minHeight: '120px', padding: '8px', border: '1px solid rgba(128,128,128,0.35)' }}
                            />
                            {!stripeElementsInfo && !errorMessage && (
                                <div style={{ fontSize: '12px', opacity: 0.6, marginTop: '4px' }}>
                                    {t('PAYMENT_LOADING') || 'Loading payment form...'}
                                </div>
                            )}
                        </div>
                    </div>

                {errorMessage && (
                    <div style={{ color: 'var(--color-error-bright)', fontSize: '13px' }}>{errorMessage}</div>
                )}

                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                    <TrebbleButton onClick={handleBackToStep1} disabled={isLoading} useEditorTheme={useEditorTheme}>
                        {t('BACK') || 'Back'}
                    </TrebbleButton>
                    <TrebbleButton
                        type="primary"
                        onClick={handleCompletePayment}
                        loading={isLoading}
                        disabled={!stripeElementsInfo || isLoading}
                        useEditorTheme={useEditorTheme}
                    >
                        {t('SUBSCRIBE') || 'Subscribe'}
                    </TrebbleButton>
                </div>
            </div>
        );
    };

    const renderStep3 = () => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div style={{ textAlign: 'center', padding: '16px 0' }}>
                <div style={{ display: 'inline-block', padding: '4px 16px', backgroundColor: 'rgba(248,137,35,0.12)', color: 'var(--trebble-primary)', fontSize: '13px', fontWeight: 600, marginBottom: '12px' }}>
                    {t('WORKSPACE_CREATED') || 'Workspace created!'}
                </div>
                <div style={{ fontSize: '14px', opacity: 0.75 }}>{workspaceName}</div>
            </div>

            <div>
                <div style={{ fontSize: '14px', fontWeight: 500, marginBottom: '12px' }}>
                    {t('INVITE_YOUR_TEAM') || 'Invite your team'}
                </div>
                {inviteEmails.map((email, index) => (
                    <div key={index} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
                        <TrebbleInput
                            value={email}
                            onChange={(e) => handleEmailChange(index, e.target.value)}
                            placeholder={t('INVITE_EMAIL_PLACEHOLDER') || 'Enter email address'}
                            useEditorTheme={useEditorTheme}
                            style={{ flex: 1 }}
                        />
                        {inviteEmails.length > 1 && (
                            <TrebbleButton
                                type="text"
                                size="small"
                                onClick={() => handleRemoveEmail(index)}
                                useEditorTheme={useEditorTheme}
                                aria-label={t('REMOVE') || 'Remove'}
                                style={{ minWidth: 0, padding: '0 6px' }}
                            >
                                <span style={{ fontSize: '16px', lineHeight: 1 }}>&#x2715;</span>
                            </TrebbleButton>
                        )}
                    </div>
                ))}
                <TrebbleButton
                    type="dashed"
                    size="small"
                    onClick={handleAddEmailField}
                    useEditorTheme={useEditorTheme}
                    style={{ marginTop: '4px' }}
                >
                    + {t('ADD_ANOTHER') || 'Add another'}
                </TrebbleButton>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <TrebbleButton onClick={onClose} useEditorTheme={useEditorTheme} disabled={isLoading}>
                    {t('SKIP_FOR_NOW') || 'Skip for now'}
                </TrebbleButton>
                <TrebbleButton type="primary" onClick={submitInvites} useEditorTheme={useEditorTheme} loading={isLoading}>
                    {t('DONE') || 'Done'}
                </TrebbleButton>
            </div>
        </div>
    );

    return (
        <TrebbleCard useEditorTheme={useEditorTheme} style={{ padding: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <ModalHeader
                    title={isInPlace
                        ? (t('UPGRADE_PLAN_TITLE') || 'Upgrade Plan')
                        : (t('UPGRADE_TITLE') || 'Create Workspace')}
                    useEditorTheme={useEditorTheme}
                    marginBottom={0}
                />
                <div style={{ fontSize: '12px', opacity: 0.6 }}>{stepLabelText}</div>
            </div>
            <div style={{ display: 'flex', gap: '4px', marginBottom: '24px' }}>
                {[1, 2, 3].map((s) => (
                    <div
                        key={s}
                        style={{
                            flex: 1,
                            height: '3px',
                            backgroundColor: s <= step ? 'var(--trebble-primary)' : 'rgba(128,128,128,0.25)',
                            transition: 'background-color 0.3s',
                        }}
                    />
                ))}
            </div>
            {step === 1 && renderStep1()}
            {step === 2 && renderStep2()}
            {step === 3 && renderStep3()}
        </TrebbleCard>
    );
};

UpgradeToTeamContent.propTypes = {
    onStartUpgrade: PropTypes.func.isRequired,
    onMountStripeElement: PropTypes.func.isRequired,
    onCompletePayment: PropTypes.func.isRequired,
    onFinalizeUpgrade: PropTypes.func.isRequired,
    onCancelUpgrade: PropTypes.func.isRequired,
    onInvite: PropTypes.func,
    onClose: PropTypes.func.isRequired,
    i18nModule: PropTypes.object.isRequired,
    useEditorTheme: PropTypes.bool,
    defaultName: PropTypes.string,
    mode: PropTypes.oneOf(['create', 'upgrade_in_place']),
};

export { UpgradeToTeamContent };
export default UpgradeToTeamContent;
